import React from 'react';

interface HeatMapCell {
  day: string;
  hour: number;
  value: number;
}

interface UsageHeatMapProps {
  data: HeatMapCell[];
}

export const UsageHeatMap: React.FC<UsageHeatMapProps> = ({ data }) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  // Find max value for scaling
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  const getCellColor = (value: number): string => {
    if (value === 0) return 'bg-[var(--surface)]';
    const intensity = value / maxValue;
    
    if (intensity > 0.75) return 'bg-primary-600';
    if (intensity > 0.5) return 'bg-primary-400';
    if (intensity > 0.25) return 'bg-primary-200';
    return 'bg-primary-100';
  };
  
  const getCellValue = (day: string, hour: number): number => {
    const cell = data.find(d => d.day === day && d.hour === hour);
    return cell?.value || 0;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 text-xs text-charcoal-600">
        <span>Less</span>
        <div className="flex gap-1">
          <div className="w-4 h-4 bg-[var(--surface)] border border-[var(--rule)] rounded"></div>
          <div className="w-4 h-4 bg-primary-100 rounded"></div>
          <div className="w-4 h-4 bg-primary-200 rounded"></div>
          <div className="w-4 h-4 bg-primary-400 rounded"></div>
          <div className="w-4 h-4 bg-primary-600 rounded"></div>
        </div>
        <span>More</span>
      </div>
      
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          <div className="flex">
            {/* Day labels */}
            <div className="flex flex-col justify-start pt-6">
              {days.map(day => (
                <div
                  key={day}
                  className="h-4 flex items-center text-xs text-charcoal-600 font-medium mb-1"
                >
                  {day}
                </div>
              ))}
            </div>
            
            {/* Hour columns */}
            <div className="flex-1 ml-2">
              {/* Hour labels */}
              <div className="flex mb-1">
                {hours.map(hour => (
                  hour % 3 === 0 ? (
                    <div
                      key={hour}
                      className="flex-1 text-center text-xs text-charcoal-600"
                      style={{ minWidth: '16px' }}
                    >
                      {hour}
                    </div>
                  ) : (
                    <div key={hour} className="flex-1" style={{ minWidth: '16px' }}></div>
                  )
                ))}
              </div>
              
              {/* Heat map grid */}
              {days.map(day => (
                <div key={day} className="flex gap-1 mb-1">
                  {hours.map(hour => {
                    const value = getCellValue(day, hour);
                    return (
                      <div
                        key={hour}
                        className={`flex-1 h-4 rounded ${getCellColor(value)} border border-[var(--rule)] cursor-pointer hover:ring-2 hover:ring-primary-500 transition-all`}
                        style={{ minWidth: '16px' }}
                        title={`${day} ${hour}:00 - ${value} visits`}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

